import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import AppLayout from "../layout/AppLayout";
import Schools from "../pages/schools/Schools";
import ExamReceipts from "../pages/reports/ExamReceipts";
import AdminPayments from "../pages/payments/AdminPayments";

// Lazy load components for better performance
const SuperAdminDashboard = lazy(
  () => import("../pages/dashboard/SuperAdminDashboard")
);

// Import NotFound component
const NotFound = lazy(() => import("../pages/errors/NotFound"));

// Reports
const ExamLabels = lazy(() => import("../pages/reports/ExamLabels"));
const ParcelingList = lazy(() => import("../pages/reports/ParcelingList"));
const SchoolReports = lazy(() => import("../pages/reports/SchoolReports"));
const CombinedReports = lazy(() => import("../pages/reports/CombinedReports"));
const ExamRegistrationForms = lazy(
  () => import("../pages/exams/ExamRegistrationForms")
);
const PastExamQuestions = lazy(
  () => import("../pages/exams/PastExamQuestions")
);
const SchemesOfWork = lazy(() => import("../pages/exams/SchemesOfWork"));
const LandingStats = lazy(() => import("../pages/landing/LandingStats"));
const LandingServices = lazy(() => import("../pages/landing/LandingServices"));
const TrainingPrograms = lazy(
  () => import("../pages/landing/TrainingPrograms")
);
const Testimonials = lazy(() => import("../pages/landing/Testimonials"));
const Departments = lazy(() => import("../pages/landing/Departments"));
const Announcements = lazy(() => import("../pages/landing/Announcements"));
const HeroSections = lazy(() => import("../pages/landing/HeroSections"));
const FooterContacts = lazy(() => import("../pages/landing/FooterContacts"));
const FooterSections = lazy(() => import("../pages/landing/FooterSections"));

// Exams setup
const Subjects = lazy(() => import("../pages/exams/Subjects"));
const ExamTypes = lazy(() => import("../pages/exams/ExamTypes"));
const ExamSessions = lazy(() => import("../pages/exams/ExamSessions"));
const ExamCategories = lazy(() => import("../pages/exams/ExamCategories"));

// User management
const Profile = lazy(() => import("../pages/users/Profile"));
const UserList = lazy(() => import("../components/users/UserList"));
const UserForm = lazy(() => import("../components/users/UserForm"));

// Loading component for Suspense fallback
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-full">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
  </div>
);

const AdminRoutes = () => {
  return (
    <Routes>
      <Route path="/" element={<AppLayout />}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route
          path="dashboard"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <SuperAdminDashboard />
            </Suspense>
          }
        />

        <Route path="exams">
          <Route path="types" element={<ExamTypes />} />
          <Route path="subjects" element={<Subjects />} />
          <Route path="sessions" element={<ExamSessions />} />
          <Route path="categories" element={<ExamCategories />} />
          <Route
            path="registration-forms"
            element={<ExamRegistrationForms />}
          />
          <Route path="past-exam-questions" element={<PastExamQuestions />} />
          <Route path="schemes-of-work" element={<SchemesOfWork />} />
        </Route>

        <Route path="reports">
          <Route path="labels" element={<ExamLabels />} />
          <Route path="parceling-list" element={<ParcelingList />} />
          <Route path="schools" element={<SchoolReports />} />
          <Route path="receipts" element={<ExamReceipts />} />
          <Route path="combined" element={<CombinedReports />} />
        </Route>

        <Route path="users">
          <Route index element={<UserList />} />
          <Route path="create" element={<UserForm />} />
          <Route path=":id/edit" element={<UserForm />} />
        </Route>

        <Route path="schools">
          <Route index element={<Schools />} />
        </Route>

        <Route path="profile" element={<Profile />} />
        <Route path="payments" element={<AdminPayments />} />

        <Route path="landing">
          <Route path="stats" element={<LandingStats />} />
          <Route path="services" element={<LandingServices />} />
          <Route path="training-programs" element={<TrainingPrograms />} />
          <Route path="testimonials" element={<Testimonials />} />
          <Route path="departments" element={<Departments />} />
          <Route path="announcements" element={<Announcements />} />
          <Route path="hero-sections" element={<HeroSections />} />
          <Route path="footer-contacts" element={<FooterContacts />} />
          <Route path="footer-sections" element={<FooterSections />} />
        </Route>

        {/* Catch-all route for super-admin section - redirects to NotFound */}
        <Route
          path="*"
          element={
            <Suspense fallback={<LoadingFallback />}>
              <NotFound />
            </Suspense>
          }
        />
      </Route>
    </Routes>
  );
};

export default AdminRoutes;
